import { useMemo } from 'react'
import { useIntl } from 'react-intl'

export interface AirportTransferFAQ {
  id: string
  question: string
  answers: Array<string>
}

function useAirportTransferFAQs(): Array<AirportTransferFAQ> {
  const intl = useIntl()

  return useMemo(() => [
    {
      id: 'private-and-shared-transfers',
      question: intl.formatMessage({
        id: '3qmvsKkT9SsWMWsBaLiV',
        defaultMessage: 'Do you offer private and shared airport transfers?',
        description: 'Airport transfer FAQ question about private and shared transfer availability',
      }),
      answers: [
        intl.formatMessage({
          id: 'NsT7apG6Lw3tSIwBnr0h',
          defaultMessage: 'Yes. Private transfers and shared shuttle transfers are available, depending on your destination and availability.',
          description: 'First airport transfer FAQ answer paragraph about transfer availability',
        }),
        intl.formatMessage({
          id: 'zGjkMtKjdwllifMV3F0S',
          defaultMessage: 'Private transfers are reserved for you and your travelling party, while shared transfers may include other passengers travelling on a similar route.',
          description: 'Second airport transfer FAQ answer paragraph about shared transfers',
        }),
      ],
    },
    {
      id: 'transfer-price',
      question: intl.formatMessage({
        id: 'wOJvC5__5ViMtPcNkDq5',
        defaultMessage: 'How is the transfer price calculated?',
        description: 'Airport transfer FAQ question about how the transfer price is calculated',
      }),
      answers: [intl.formatMessage({
        id: 'g_334PEeX_qZ-j1kw1Aq',
        defaultMessage: 'We always show the total price for your booking. For private transfers, the price is calculated per vehicle, regardless of the number of passengers, as long as everyone fits within the vehicle’s passenger and luggage limits. For shared transfers, the price is calculated per person, and the total shown reflects the number of passengers in your booking. Please note that optional extras that you select may incur an additional charge, however this will be displayed to you during the booking process.',
        description: 'Airport transfer FAQ answer explaining private and shared pricing and possible additional charges for optional extras',
      })],
    },
    {
      id: 'vehicle-shown',
      question: intl.formatMessage({
        id: 'Ri_L8g3gdRflosT2Ol4Y',
        defaultMessage: 'Will I get the exact vehicle shown when I book?',
        description: 'Airport transfer FAQ question about receiving the vehicle shown at booking',
      }),
      answers: [intl.formatMessage({
        id: 'y93wfFitPCz-HnGmG081',
        defaultMessage: 'The vehicle image and model shown are examples of what you can expect, such as Kia Carnival or similar. The exact make and model may vary, but your booked vehicle category, passenger capacity and luggage capacity will be suitable for your booking.',
        description: 'Airport transfer FAQ answer explaining that the exact vehicle make and model may vary',
      })],
    },
    {
      id: 'passenger-capacity',
      question: intl.formatMessage({
        id: '7WLa9M2iKIxPjF5DnBvD',
        defaultMessage: 'How many passengers can travel in my transfer?',
        description: 'Airport transfer FAQ question about passenger capacity for a transfer',
      }),
      answers: [intl.formatMessage({
        id: 'n-ru7onjFYXswZAd07Jd',
        defaultMessage: 'Each transfer option shows its maximum passenger capacity. Make sure you enter the correct number of travellers when booking so you are matched with a suitable vehicle.',
        description: 'Airport transfer FAQ answer explaining how passenger capacity determines a suitable vehicle',
      })],
    },
    {
      id: 'luggage-allowance',
      question: intl.formatMessage({
        id: '2QRf7H3iEZhe5R3nvPge',
        defaultMessage: 'How much luggage can I bring?',
        description: 'Airport transfer FAQ question about luggage allowance for a transfer',
      }),
      answers: [intl.formatMessage({
        id: 'QdYSg80pPNs6QBZ_vfzw',
        defaultMessage: 'Each vehicle option shows the total number of luggage items it can accommodate. Choose a vehicle with enough capacity for everyone in your group and their luggage. Luggage capacity is shown as a number of bags, rather than specific bag dimensions.',
        description: 'Airport transfer FAQ answer explaining how to choose a vehicle with sufficient luggage capacity',
      })],
    },
    {
      id: 'child-seats',
      question: intl.formatMessage({
        id: 'MwnWZrx36eb_m89hGNPj',
        defaultMessage: 'Are baby and child car seats available?',
        description: 'Airport transfer FAQ question about available baby, child, and booster seats',
      }),
      answers: [intl.formatMessage({
        id: '6rX50AKasLCOjHIm74CE',
        defaultMessage: 'Baby seats, child seats and booster seats may be available as optional extras. Availability depends on the transfer and vehicle you select, so check the available extras when booking.',
        description: 'Airport transfer FAQ answer explaining how child-seat extras depend on the selected transfer',
      })],
    },
    {
      id: 'payment',
      question: intl.formatMessage({
        id: 'BTW6_DhgYRcf2hgqewyE',
        defaultMessage: 'How do I pay for my airport transfer?',
        description: 'Airport transfer FAQ question about payment for an airport transfer booking',
      }),
      answers: [intl.formatMessage({
        id: 'O0DTDR0YROrq_xQwNKss',
        defaultMessage: 'Your transfer is paid in full at checkout. Deposits and payment to the driver at pick-up are not currently supported as standard payment options. If you request additional waiting time after your included waiting period has ended, your driver may offer this for an additional fee that may need to be paid directly to the driver.',
        description: 'Airport transfer FAQ answer explaining standard payment and possible additional waiting-time charges',
      })],
    },
    {
      id: 'flight-delayed',
      question: intl.formatMessage({
        id: 'W7yY4xqwDM1c5k50ST2a',
        defaultMessage: 'What happens if my flight is delayed?',
        description: 'Airport transfer FAQ question about a delayed arriving flight',
      }),
      answers: [intl.formatMessage({
        id: 'DjvAMRShcuB4nzYybJZo',
        defaultMessage: 'Your transfer includes up to 3 hours of flight delay protection, so your driver will still be there if you arrive a little late. If your flight is delayed by more than 3 hours, your transfer is cancelled automatically.',
        description: 'Airport transfer FAQ answer explaining the protection available when an arriving flight is delayed',
      })],
    },
    {
      id: 'driver-wait-time',
      question: intl.formatMessage({
        id: 'fc3RH6X2KalvPshAx5nO',
        defaultMessage: 'How long will my driver wait after my flight lands?',
        description: 'Airport transfer FAQ question about driver waiting time after landing',
      }),
      answers: [intl.formatMessage({
        id: 'pc6npKdaqmAghU5J-Gko',
        defaultMessage: 'Your driver will wait up to 45 minutes after your flight lands, at no extra cost — plenty of time to clear passport control and collect your bags.',
        description: 'Airport transfer FAQ answer explaining airport buffer time and the included driver waiting period',
      })],
    },
    {
      id: 'luggage-and-immigration-delay',
      question: intl.formatMessage({
        id: 'q-Z0dDpNb3cbS5zhp5XN',
        defaultMessage: 'What if I’m delayed collecting my luggage or getting through immigration?',
        description: 'Airport transfer FAQ question about luggage collection or immigration delays',
      }),
      answers: [intl.formatMessage({
        id: 'HKCQxOza7E8MJeFIZABV',
        defaultMessage: 'Your driver will wait up to 45 minutes after your flight lands, at no extra cost. If you still cannot meet your driver after this time, contact them or support as soon as possible.',
        description: 'Airport transfer FAQ answer explaining the allowance for luggage collection or immigration delays',
      })],
    },
    {
      id: 'flight-cancelled',
      question: intl.formatMessage({
        id: 'vBLmT9lrgIZudsylPN7n',
        defaultMessage: 'What happens if my flight is cancelled?',
        description: 'Airport transfer FAQ question about a cancelled arriving flight',
      }),
      answers: [intl.formatMessage({
        id: 'A88pIv5CYgL720Ynvb9O',
        defaultMessage: 'You can cancel your transfer for free if you cancel at least 24 hours before your scheduled pick-up time. If your flight is cancelled within 24 hours of your pick-up, the standard cancellation policy applies and the transfer generally will not be eligible for free cancellation. Depending on your circumstances, you may want to check whether the cost is covered by your travel insurance.',
        description: 'Airport transfer FAQ answer explaining cancellation options when a customer flight is cancelled',
      })],
    },
    {
      id: 'cancel-transfer',
      question: intl.formatMessage({
        id: 'gxcHm4a4_oIYlhCoaVA4',
        defaultMessage: 'Can I cancel my airport transfer?',
        description: 'Airport transfer FAQ question about cancelling an airport transfer booking',
      }),
      answers: [intl.formatMessage({
        id: 'SYe48nsCMlgr4jlmazcG',
        defaultMessage: 'Yes. You can cancel for free up to 24 hours before your scheduled pick-up time. Cancellations made within 24 hours of pick-up are not eligible for free cancellation.',
        description: 'Airport transfer FAQ answer explaining the free cancellation deadline for a transfer booking',
      })],
    },
    {
      id: 'no-show',
      question: intl.formatMessage({
        id: 'LXV1qFWuBkt_XHN3LlYm',
        defaultMessage: 'What happens if I don’t arrive for my transfer?',
        description: 'Airport transfer FAQ question about a passenger not arriving for transfer',
      }),
      answers: [intl.formatMessage({
        id: 'O_Tllk24guRW8c9SJNdF',
        defaultMessage: 'For airport pick-ups, your driver will wait up to 45 minutes after your flight lands, free of charge. If you have not met your driver by the end of the included waiting period, the driver may declare the booking a no-show and leave.',
        description: 'Airport transfer FAQ answer explaining what happens when a customer does not meet the driver',
      })],
    },
    {
      id: 'driver-no-show',
      question: intl.formatMessage({
        id: 'SBFi329btxMga2Ry0HX0',
        defaultMessage: 'What happens if my driver doesn’t arrive?',
        description: 'Airport transfer FAQ question about a driver not arriving for transfer',
      }),
      answers: [intl.formatMessage({
        id: 'ehrmfTxRvVYoDHCxnKJF',
        defaultMessage: 'Should you have trouble locating your driver or they have not arrived you can contact the driver directly. The contact number will be displayed in the Journey Details link sent to you. If you need further assistance, contact us as soon as possible. The transfer provider will investigate what happened using information from both you and the driver. Investigations can take up to 96 hours. If the investigation confirms that the driver did not provide the booked service, a refund will be arranged.',
        description: 'Airport transfer FAQ answer about contacting the driver and support, investigation, and refund when a driver does not arrive',
      })],
    },
    {
      id: 'driver-confirmation',
      question: intl.formatMessage({
        id: 'yLTYwXuxOlqnXk0hH31o',
        defaultMessage: 'When will I get my transfer and driver confirmation?',
        description: 'Airport transfer FAQ question about when driver and journey details will be confirmed and sent',
      }),
      answers: [intl.formatMessage({
        id: 'JIEB2g7wf7r98VBOdj0X',
        defaultMessage: 'We will email your driver and journey information closer to your departure as soon as they are confirmed. 24 hours prior to your transfer you will also receive a WhatsApp/SMS with your confirmed driver and journey details. Journey details will allow you to track your transfer, view meeting point information and your driver information and contact details.',
        description: 'Airport transfer FAQ answer explaining email and WhatsApp or SMS confirmation timing and journey details',
      })],
    },
    {
      id: 'find-driver-at-airport',
      question: intl.formatMessage({
        id: '9BRkcKdgABhyNtvtmFhM',
        defaultMessage: 'How do I find my driver at the airport?',
        description: 'Airport transfer FAQ question about finding the driver at the airport pickup location',
      }),
      answers: [intl.formatMessage({
        id: 'q7lEsE9r04W7gW6yqqPo',
        defaultMessage: 'You will be sent confirmation of where to meet your driver prior to your booked transfer. This can be found in the Journey Details link sent to you to directly track your driver and transfer. Dependent on the arrival airport, your pickup may be via a service desk, meet and greet (optional extra) at arrivals or from a kerbside pickup zone.',
        description: 'Airport transfer FAQ answer explaining journey details and possible airport driver meeting points',
      })],
    },
    {
      id: 'airport-bound-pickup-time',
      question: intl.formatMessage({
        id: 'REidhr7fmpoxVvDw1O4j',
        defaultMessage: 'What time should I book my transfer going to the airport?',
        description: 'Airport transfer FAQ question about choosing a pickup time for a transfer going to the airport',
      }),
      answers: [intl.formatMessage({
        id: 'gP2Q3wivShHk7IYh1Znd',
        defaultMessage: 'When you are making your booking, the time you request for pickup to the airport is the time your driver will be booked to arrive at your location. You will need to factor in travel time to the airport when requesting your pickup time to make sure you arrive at the airport in time for your flight.',
        description: 'Airport transfer FAQ answer explaining how to allow travel time when requesting airport-bound pickup',
      })],
    },
    {
      id: 'optional-extras',
      question: intl.formatMessage({
        id: 'd1rfGPjSK6HTRFgIkWMk',
        defaultMessage: 'What are optional extras?',
        description: 'Airport transfer FAQ question about available optional extras during the booking process',
      }),
      answers: [intl.formatMessage({
        id: '74OisFPpAjIJowN5cYjp',
        defaultMessage: 'Dependent on the airport or location of your transfer optional extras may be available to add to your journey at an additional cost. Extras available will be displayed during the booking process. Please note some extras such as additional stop, pets, and large or oversized luggage require you enter details on your request in the driver comments section. For more information relating to extras click on the information icon next to the optional extra.',
        description: 'Airport transfer FAQ answer describing optional extras, possible charges, and driver comment requirements',
      })],
    },
    {
      id: 'driver-wait-time-all-pickups',
      question: intl.formatMessage({
        id: 'Cgg510_yuaNbc_T8TT-V',
        defaultMessage: 'How long will a driver wait?',
        description: 'Airport transfer FAQ question about included driver waiting time at airport and other pickups',
      }),
      answers: [intl.formatMessage({
        id: 'gBQggKIhDJgM-RGC5r27',
        defaultMessage: 'For pickup from an airport, your transfer includes up to 45 minutes of free waiting time. When being picked up from another location such as a hotel, your transfer includes up to 15 minutes of free waiting time.',
        description: 'Airport transfer FAQ answer comparing free waiting time at airport and other location pickups',
      })],
    },
  ], [intl])
}

export default useAirportTransferFAQs
